import React, { useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpFromBracket,
  faFileLines,
} from '@fortawesome/pro-light-svg-icons';
import { faTriangleExclamation } from '@fortawesome/pro-duotone-svg-icons';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import NewModal from '../../Shared/NewModal';
import { uploadMedicationFile } from '../../../store/pharmacyPatients/actionCreators';
import {
  CsvIssue,
  MedicationUploadResult,
} from '../../../store/pharmacyPatients/pharmacyPatientsTypes';
import {
  customError,
  dimGrey,
  lightBlack,
  lightGrey,
  lightTeal,
  warningYellow,
} from '../../../mainTheme';
import { ALLOWED_UPLOAD_EXTENSIONS } from '../mockData';

interface MedicationUploadModalProps {
  patientId: number;
  onClose: () => void;
  onUploadComplete: () => void;
}

const ACCEPT_ATTR = '.csv,.pgp,.gpg';

// Issues reported by the upload API. "confirm" issues are repairable — the
// user may sanitize & re-upload; "blocked" issues are not, the only way
// forward is a corrected file.
interface CsvIssueReport {
  kind: 'confirm' | 'blocked';
  message?: string;
  issues: CsvIssue[];
}

const typography = {
  heading: {
    fontWeight: 500,
    fontSize: '22px',
    lineHeight: '30px',
    letterSpacing: '0.36px',
    color: lightBlack,
  },
  subText: {
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '22px',
    letterSpacing: '0.2px',
    color: dimGrey,
  },
} as const;

const getExtension = (fileName: string): string =>
  fileName.split('.').pop()?.toLowerCase() ?? '';

const isAllowed = (fileName: string): boolean =>
  (ALLOWED_UPLOAD_EXTENSIONS as string[]).includes(getExtension(fileName));

const MedicationUploadModal: React.FC<MedicationUploadModalProps> = ({
  patientId,
  onClose,
  onUploadComplete,
}) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [issueReport, setIssueReport] = useState<CsvIssueReport | null>(null);

  const handleFileSelected = (file: File) => {
    if (!isAllowed(file.name)) {
      const message = 'Only CSV, PGP, and GPG files are supported.';
      setError(message);
      setSelectedFile(null);
      toast.error(message);
      return;
    }
    setError(null);
    setSelectedFile(file);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) {
      handleFileSelected(file);
    }
  };

  const handleUpload = async (sanitize = false) => {
    if (!selectedFile) {
      return;
    }
    setIsUploading(true);

    const result = (await dispatch(
      uploadMedicationFile(patientId, selectedFile, sanitize) as any,
    )) as unknown as MedicationUploadResult;

    if (result.outcome === 'issues_found') {
      // The server rejected the CSV but can repair it; ask the user first.
      setIssueReport({ kind: 'confirm', issues: result.issues });
      setIsUploading(false);
      return;
    }

    if (result.outcome === 'blocked') {
      // The server cannot repair this file; report why and require a new one.
      setIssueReport({
        kind: 'blocked',
        message: result.message,
        issues: result.issues,
      });
      setIsUploading(false);
      return;
    }

    if (result.outcome === 'error') {
      // Thunk already surfaced the error toast; keep the modal open to retry.
      setIsUploading(false);
      return;
    }

    // Let the parent refresh the history from the server.
    onUploadComplete();
    setIsUploading(false);
    onClose();
  };

  if (issueReport) {
    const isBlocked = issueReport.kind === 'blocked';
    const dismissReport = () => {
      setIssueReport(null);
      if (isBlocked) {
        // The file can never upload as-is; make the user pick a new one.
        setSelectedFile(null);
      }
    };
    return (
      <NewModal
        showCloseX
        title="Upload new medication"
        maxWidth="xs"
        onClose={dismissReport}
        actions={{
          ...(!isBlocked && {
            secondaryAction: {
              text: 'Cancel',
              onClick: dismissReport,
            },
          }),
          primaryAction: isBlocked
            ? {
                text: 'Choose a different file',
                onClick: dismissReport,
              }
            : {
                text: 'Sanitize & upload',
                onClick: () => {
                  setIssueReport(null);
                  handleUpload(true);
                },
              },
        }}
      >
        <Box>
          <FontAwesomeIcon
            icon={faTriangleExclamation}
            style={{ width: 40, height: 40, color: warningYellow }}
          />
          <Box display="flex" flexDirection="column" gap={1.5} mt={2}>
            <Typography sx={typography.heading}>Invalid file</Typography>
            <Typography sx={typography.subText}>
              {isBlocked
                ? (issueReport.message ??
                  'This file cannot be uploaded. Please correct the file and upload again.')
                : 'Invalid file, would you like to sanitize and upload? Upload would not be successful without sanitizing.'}
            </Typography>
          </Box>
          {issueReport.issues.length > 0 && (
            <Box
              sx={{
                mt: 3,
                mb: 1,
                p: '16px',
                borderRadius: '12px',
                backgroundColor: '#F6F6F9',
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <FontAwesomeIcon
                  icon={faFileLines}
                  style={{ width: 16, height: 16, color: dimGrey }}
                />
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: '14px',
                    lineHeight: '18px',
                    color: lightBlack,
                    wordBreak: 'break-all',
                  }}
                >
                  {selectedFile?.name}
                </Typography>
              </Box>
              {issueReport.issues.map((issue) => (
                <Typography
                  key={issue.code}
                  sx={{
                    fontWeight: 500,
                    fontSize: '14px',
                    lineHeight: '20px',
                    color: dimGrey,
                  }}
                >
                  &bull; {issue.label}
                  {issue.affected_rows &&
                    issue.affected_rows.length > 0 &&
                    ` (rows ${issue.affected_rows.join(', ')})`}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      </NewModal>
    );
  }

  return (
    <NewModal
      showCloseX
      title="Upload new medication"
      maxWidth="sm"
      onClose={onClose}
      isLoading={isUploading}
      actions={{
        secondaryAction: {
          text: 'Cancel',
          onClick: onClose,
        },
        primaryAction: {
          text: 'Upload',
          onClick: () => handleUpload(),
          disabled: !selectedFile || isUploading,
        },
      }}
    >
      <Box display="flex" flexDirection="column" gap={2}>
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: '16px',
            lineHeight: '22px',
            letterSpacing: '0.2px',
            color: dimGrey,
          }}
        >
          Drag and drop or click to upload. Only CSV, PGP, and GPG files are
          supported.
        </Typography>

        <Box
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            p: '32px',
            borderRadius: '12px',
            border: `1px dashed ${isDragging ? lightTeal : lightGrey}`,
            backgroundColor: isDragging
              ? 'rgba(107, 202, 186, 0.08)'
              : '#F6F6F9',
            cursor: 'pointer',
            textAlign: 'center',
          }}
        >
          <FontAwesomeIcon
            icon={selectedFile ? faFileLines : faArrowUpFromBracket}
            style={{ width: 40, height: 40, color: lightGrey }}
          />
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '16px',
              color: lightBlack,
              wordBreak: 'break-all',
            }}
          >
            {selectedFile ? selectedFile.name : 'Choose a file or drag it here'}
          </Typography>
          <Typography
            sx={{ fontWeight: 500, fontSize: '14px', color: dimGrey }}
          >
            CSV, PGP, or GPG
          </Typography>

          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept={ACCEPT_ATTR}
            onChange={(e) => {
              if (e.target.files && e.target.files[0]) {
                handleFileSelected(e.target.files[0]);
              }
              // reset so selecting the same file again re-triggers onChange
              e.target.value = '';
            }}
          />
        </Box>

        {error && (
          <Typography
            sx={{ fontWeight: 500, fontSize: '14px', color: customError }}
          >
            {error}
          </Typography>
        )}
      </Box>
    </NewModal>
  );
};

export default MedicationUploadModal;
